import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getEvents } from '../services/firestoreService';
import { getUpcomingEvents, getPastEvents } from '../data/eventData';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../hooks/useSEO';

const Events = () => {
  const { hasRole, currentUser, isLoggedIn } = useAuth();

  // SEO optimization for events page
  useSEO();

  // State for events data
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useFirebase, setUseFirebase] = useState(true);

  // Track if we've loaded once to prevent reverting to static events after deletions
  const hasLoadedOnce = useRef(false);

  // Function to get the appropriate price range for display
  const getDisplayPrice = (event) => {
    // Handle new pricing structure - EXCLUDE sponsor pricing
    if (event.pricing) {
      const prices = [];
      if (event.pricing.member !== undefined) prices.push(event.pricing.member);
      if (event.pricing.nonMember !== undefined) prices.push(event.pricing.nonMember);
      // NOTE: Intentionally excluding sponsor pricing - sponsors use discount codes

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        if (minPrice === maxPrice) {
          return `$${minPrice.toFixed(2)}`;
        } else {
          return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
        }
      }
    }

    // Handle legacy ticketTypes structure
    if (event.ticketTypes && Array.isArray(event.ticketTypes)) {
      const prices = event.ticketTypes.map(ticket => ticket.price).filter(price => price !== undefined);

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        if (minPrice === maxPrice) {
          return `$${minPrice.toFixed(2)}`;
        } else {
          return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
        }
      }
    }

    // Fallback to original price or default
    return event.price || 'Contact for pricing';
  };

  // Load events from Firebase or fallback to static data
  useEffect(() => {
    loadEvents();
  }, [useFirebase]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      if (useFirebase) {
        console.log('Loading events from Firebase...');

        // Load upcoming events (future events)
        const upcomingData = await getEvents(20, true);
        console.log('Upcoming events from Firebase:', upcomingData);

        // Load past events (all events, then filter for past ones)
        const allEventsData = await getEvents(50, false);
        const pastData = allEventsData.filter(event => {
          const eventDate = event.startDate?.toDate ? event.startDate.toDate() : new Date(event.startDate);
          return eventDate < new Date();
        });
        console.log('Past events from Firebase:', pastData);

        // Sort upcoming events by date (earliest first)
        const sortedUpcoming = upcomingData.sort((a, b) => {
          const dateA = a.startDate?.toDate ? a.startDate.toDate() : new Date(a.startDate || a.date);
          const dateB = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate || b.date);
          return dateA - dateB;
        });

        // Sort past events by date (most recent first)
        const sortedPast = pastData.sort((a, b) => {
          const dateA = a.startDate?.toDate ? a.startDate.toDate() : new Date(a.startDate || a.date);
          const dateB = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate || b.date);
          return dateB - dateA;
        });

        // Set events from Firebase (empty arrays if no events found)
        setUpcomingEvents(sortedUpcoming);
        setPastEvents(sortedPast);

        // Only add static events if this is the initial load and no events exist at all
        // This prevents reverting to static events after deletions
        if (upcomingData.length === 0 && pastData.length === 0 && !hasLoadedOnce.current) {
          console.log('Initial load: No events found in Firebase, adding static events...');
          const staticUpcoming = getUpcomingEvents();
          const staticPast = getPastEvents();
          setUpcomingEvents(staticUpcoming);
          setPastEvents(staticPast);
        }

        hasLoadedOnce.current = true;
      } else {
        console.log('Loading events from static data...');
        // Fallback to static data
        const upcoming = getUpcomingEvents();
        const past = getPastEvents();
        setUpcomingEvents(upcoming);
        setPastEvents(past);
      }
    } catch (err) {
      console.error('Error loading events:', err);
      setError(err.message);

      // Fallback to static data on error
      if (useFirebase) {
        console.log('Firebase error, falling back to static data...');
        setUseFirebase(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // State for event filtering
  const [eventFilter, setEventFilter] = useState('upcoming'); // 'upcoming' or 'past'
  const [searchTerm, setSearchTerm] = useState('');

  // Filter events based on search term
  const filteredUpcomingEvents = upcomingEvents.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPastEvents = pastEvents.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Events</h1>
            <p className="mt-2 text-xl">Connect, Learn, and Grow with APN</p>


          </div>
        </div>
      </div>

      {/* Events Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filter */}
          <div className="mb-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex space-x-4">
              <button
                onClick={() => setEventFilter('upcoming')}
                className={`px-4 py-2 rounded-md ${
                  eventFilter === 'upcoming'
                    ? 'bg-apn-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Upcoming Events
              </button>
              <button
                onClick={() => setEventFilter('past')}
                className={`px-4 py-2 rounded-md ${
                  eventFilter === 'past'
                    ? 'bg-apn-blue text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Past Events
              </button>

              {/* Admin Only - Create Event Button */}
              {hasRole('admin') && (
                <Link
                  to="/admin/events/create"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-200"
                >
                  Create Event
                </Link>
              )}
            </div>
            <div className="w-full md:w-64">
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>
          </div>

          {/* Events List */}
          <div>
            {eventFilter === 'upcoming' ? (
              filteredUpcomingEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredUpcomingEvents.map(event => (
                    <div key={event.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                      <div className="h-48 bg-white relative">
                        {/* Event image */}
                        <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                          <img
                            src={event.imageUrl || `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`}
                            alt={event.title}
                            className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                            }}
                          />
                        </div>
                        {event.featured && (
                          <div className="absolute top-0 left-0 bg-apn-blue text-white px-3 py-1">
                            Featured
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-2">{event.title}</h3>
                        <p className="text-gray-600 mb-4">{event.description}</p>
                        <div className="space-y-2 mb-6">
                          <div className="flex items-center text-gray-600">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{event.date}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{event.location}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{getDisplayPrice(event)}</span>
                          </div>
                        </div>
                        <Link
                          to={`/events/${event.id}`}
                          className="block w-full bg-apn-blue text-white text-center py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-xl text-gray-600">No upcoming events found matching your search.</p>
                </div>
              )
            ) : (
              filteredPastEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPastEvents.map(event => (
                    <div key={event.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                      <div className="h-48 bg-white relative">
                        {/* Event image */}
                        <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                          <img
                            src={event.imageUrl || `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`}
                            alt={event.title}
                            className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                            }}
                          />
                        </div>
                        {event.featured && (
                          <div className="absolute top-0 left-0 bg-apn-blue text-white px-3 py-1">
                            Featured
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-2">{event.title}</h3>
                        <p className="text-gray-600 mb-4">{event.description}</p>
                        <div className="space-y-2 mb-6">
                          <div className="flex items-center text-gray-600">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{event.date}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{event.location}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{getDisplayPrice(event)}</span>
                          </div>
                        </div>
                        <Link
                          to={`/events/${event.id}`}
                          className="block w-full bg-gray-500 text-white text-center py-2 px-4 rounded hover:bg-gray-600 transition duration-200"
                        >
                          View Event Recap
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-xl text-gray-600">No past events found matching your search.</p>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* Event Categories */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Event Categories</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link to="/events/category/in-person" className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition duration-200">
              <h3 className="text-xl font-bold text-apn-blue mb-2">In-Person</h3>
              <p className="text-gray-600">Networking events and meetups in various cities</p>
            </Link>
            <Link to="/events/category/virtual" className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition duration-200">
              <h3 className="text-xl font-bold text-apn-blue mb-2">Virtual</h3>
              <p className="text-gray-600">Online webinars and digital networking opportunities</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Join the Network?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with finance professionals, access exclusive resources, and accelerate your career with APN.
          </p>
          <Link to="/register" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Become a Member Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Events;
